/**
 * Lazy shell around the heavy Markdown renderer.
 *
 * The real implementation lives in MarkdownInner.jsx and pulls in
 * react-markdown + remark-gfm + react-syntax-highlighter (Prism) + the entire
 * mdast/hast/remark/rehype/micromark tree. Together those weigh ~350–400 KB
 * gzipped — far too heavy to land in the main chunk that every page must
 * download before first paint.
 *
 * By lazy()-importing the real module behind a Suspense, the main chunk loses
 * those deps. Markdown loads only when a route actually mounts a <Markdown>
 * (Q&A, articles, chat, AI helper, labs). The fallback is a tiny shimmer that
 * matches the same line height so layouts don't jump.
 *
 * Consumers do not change a single line — they keep:
 *     import Markdown from "./Markdown";
 *     <Markdown source={text} />
 */
import { lazy, Suspense } from "react";

const MarkdownInner = lazy(() => import("./MarkdownInner"));

function MarkdownShimmer() {
    return (
        <div className="space-y-2 animate-pulse" aria-hidden="true">
            <div className="h-3 bg-slate-800/60 rounded w-11/12" />
            <div className="h-3 bg-slate-800/60 rounded w-9/12" />
            <div className="h-3 bg-slate-800/60 rounded w-10/12" />
        </div>
    );
}

export default function Markdown(props) {
    return (
        <Suspense fallback={<MarkdownShimmer />}>
            <MarkdownInner {...props} />
        </Suspense>
    );
}
